import { get, isArray, isNil } from "lodash";
import React from "react";
import { FormattedMessage } from "react-intl";
import {
  AUGMENTT_POSTURE_STATES,
  MICROSOFT_POSTURE_STATES,
  POSTURE_STATE_COLOR_CODES,
  POSTURE_STATE_TO_MSG,
  SECURE_CHECK_MESSAGES,
  SECURE_CHECK_TYPES,
  SHORTCUTS,
} from "./constants";
import postureConfig from "./postureConfig";

// format single security check data as the table ones
// so we can reuse utils functions on them
export const summarizeSecurityCheck = (securityCheckData) => {
  const compliantCount = securityCheckData?.compliance?.compliant?.length ?? 0;
  const nonCompliantCount =
    securityCheckData?.compliance?.nonCompliant?.length ?? 0;
  const totalCount = securityCheckData?.compliance?.settings?.length;

  return {
    ...securityCheckData,
    status: securityCheckData?.configuration?.status,
    securityCheck: securityCheckData?.checkType,
    compliance: {
      ...securityCheckData?.compliance,
      compliant: compliantCount,
      nonCompliant: nonCompliantCount,
      total: totalCount || compliantCount + nonCompliantCount,
    },
  };
};

export const getPostureStatus = (posture) => {
  if (
    posture?.msSecureScore?.scoreControlState &&
    posture?.msSecureScore?.scoreControlState !==
      MICROSOFT_POSTURE_STATES.DEFAULT &&
    !(
      posture?.msSecureScore?.scoreControlState ===
        MICROSOFT_POSTURE_STATES.RISK_ACCEPTED &&
      posture.status === AUGMENTT_POSTURE_STATES.COMPLIANT
    )
  ) {
    return posture?.msSecureScore?.scoreControlState;
  }
  return posture.status;
};

export const getPostureStatusMessage = (posture, intl) => {
  const status = getPostureStatus(posture);
  if (POSTURE_STATE_TO_MSG[status]) {
    return intl.formatMessage({ id: POSTURE_STATE_TO_MSG[status] });
  }
  return status;
};

export const getPostureStatusDetails = (posture, intl, context = 'table') => {
  if (
    posture?.msSecureScore?.scoreControlComment &&
    posture?.msSecureScore?.scoreControlState !==
      MICROSOFT_POSTURE_STATES.DEFAULT &&
    !(
      posture?.msSecureScore?.scoreControlState ===
        MICROSOFT_POSTURE_STATES.RISK_ACCEPTED &&
      posture.status === AUGMENTT_POSTURE_STATES.COMPLIANT
    )
  ) {
    return posture?.msSecureScore?.scoreControlComment;
  }

  if (
    postureConfig?.[posture.checkId]?.statusDetailsFormatter &&
    !isNil(posture?.compliance)
  ) {
    return postureConfig?.[posture.checkId]?.statusDetailsFormatter(
      posture,
      intl,
      context
    );
  }

  if (
    posture?.compliance?.compliant &&
    posture?.compliance?.compliant === posture?.compliance?.total
  ) {
    return <FormattedMessage id="SECURE.SETTING_IN_PLACE" />;
  }

  return null;
};

export const getPostureColorCode = (posture) =>
  POSTURE_STATE_COLOR_CODES[posture.status];

export const getPostureRationale = (posture, intl) => {
  // rationale can be either a message id or a React component
  const rationale = postureConfig?.[posture.checkId]?.rationale;
  if (!rationale) {
    return null;
  }

  // rationale is a message id
  if (typeof rationale === "string") {
    return <FormattedMessage id={rationale} />;
  }

  if (typeof rationale === "function") {
    return rationale({ posture, intl });
  }

  // rationale is a React component
  return rationale;
};

const addToOptionsMap = (optionsMap, value) => {
  // eslint-disable-next-line no-param-reassign
  optionsMap[value] = optionsMap[value] ? optionsMap[value] + 1 : 1;
};

export const createOptions = (tableData, field) => {
  const optionsMap = {};

  tableData?.forEach((row) => {
    const value = get(row, field);

    if (Array.isArray(value)) {
      value.forEach((item) => {
        optionsMap[item] = optionsMap[item] ? optionsMap[item] + 1 : 1;
      });
    } else if (value) {
      addToOptionsMap(optionsMap, value);
    }
  });

  return Object.entries(optionsMap).map((entry) => ({
    label: entry[0],
    value: entry[0],
    count: entry[1],
  }));
};

export const parseHubErrorMessage = (hubError) => {
  let message = hubError;
  try {
    const errors = JSON.parse(hubError); // eslint-disable-line no-underscore-dangle
    message = errors?.[0]?.message;
  } catch (e) {
    // ignore
  }

  return message;
};

const formatSecureCheckMessageByCheckId = (checkId) =>
  `SECURE_CHECK.${checkId?.replace(/([a-z])([A-Z])/g, "$1_$2")?.toUpperCase()}`;

export const getPostureName = (checkId, intl) => {
  const messageId = SECURE_CHECK_MESSAGES[checkId];
  return messageId
    ? intl.formatMessage({ id: messageId })
    : intl.formatMessage({ id: formatSecureCheckMessageByCheckId(checkId) });
};

/* SORT FUNCTIONS START */
/*
Posture priority rules:

1. Configurable (configurable items should always be above non-configureable items)

2. MS secure score impacts (whether secure score object availbale or not)

3. Maps to a compliance standard

4. Alphabetical (once each of the above prioritization attributes have been applied)
*/
const hasCompliance = (checkId) =>
  Boolean(postureConfig?.[checkId]?.compliance);

const isConfigurable = (checkId) => {
  const shortcut = postureConfig?.[checkId]?.shortcut;

  if (isArray(shortcut)) {
    return shortcut?.some((type) => type !== SHORTCUTS.INSTRUCTIONS);
  }
  return shortcut !== SHORTCUTS.INSTRUCTIONS;
};

// Step 2: Further divide based on msSecureScore availability and sort alphabetically
const sortBySecurityScore = (a, b) => {
  if (a.msSecureScore && !b.msSecureScore) {
    return -1;
  }
  if (!a.msSecureScore && b.msSecureScore) {
    return 1;
  }
  return a?.formattedName?.localeCompare(b?.formattedName);
};

// Step 3: Separate based on cisAvailability
const categorizeByCisAvailability = (arr) => {
  const withCis = [];
  const withoutCis = [];

  arr.forEach((item) => {
    if (hasCompliance(item.checkId)) {
      withCis.push(item);
    } else {
      withoutCis.push(item);
    }
  });

  return { withCis, withoutCis };
};

// Sort not configurables based on security score and CIS availability
const sortBySecurityScoreAndCis = (a, b) => {
  if (a.msSecureScore && !b.msSecureScore) {
    return -1;
  }
  if (!a.msSecureScore && b.msSecureScore) {
    return 1;
  }
  if (a.msSecureScore && b.msSecureScore) {
    // If both have secure score, compare security check names
    return a?.formattedName?.localeCompare(b?.formattedName);
  }
  if (hasCompliance(a.checkId) && !hasCompliance(b.checkId)) {
    // If only a has CIS, prioritize it
    return -1;
  }
  if (!hasCompliance(a.checkId) && hasCompliance(b.checkId)) {
    // If only b has CIS, prioritize it
    return 1;
  }
  // If neither has secure score or CIS, compare security check names
  return a?.formattedName?.localeCompare(b?.formattedName);
};

export const sortSecurityChecks = (data, intl) => {
  const configurables = [];
  const notConfigurables = [];

  // Step 1: Separate configurables and notConfigurables
  data.forEach((item) => {
    // add new property 'formattedName' for sorting
    const formattedName = getPostureName(item.checkId, intl); // eslint-disable-line no-param-reassign

    if (isConfigurable(item.checkId)) {
      configurables.push({
        ...item,
        formattedName,
      });
    } else {
      notConfigurables.push({
        ...item,
        formattedName,
      });
    }
  });

  configurables.sort(sortBySecurityScore);

  const { withCis: configurablesWithCis, withoutCis: configurablesWithoutCis } =
    categorizeByCisAvailability(configurables);

  notConfigurables.sort(sortBySecurityScoreAndCis);

  const finalArray = [
    ...configurablesWithCis,
    ...configurablesWithoutCis,
    ...notConfigurables,
  ];

  const adminMfaIndex = finalArray.findIndex(
    (item) => item.checkId === SECURE_CHECK_TYPES.ADMIN_MFA
  );
  const userMfaIndex = finalArray.findIndex(
    (item) => item.checkId === SECURE_CHECK_TYPES.USER_MFA
  );
  if (adminMfaIndex !== -1 && userMfaIndex !== -1) {
    finalArray.splice(0, 0, finalArray.splice(adminMfaIndex, 1)[0]);
    finalArray.splice(1, 0, finalArray.splice(userMfaIndex, 1)[0]);
  }

  return finalArray;
};
/* SORT FUNCTIONS END */
